import {useMemo, useState, useCallback, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import cn from 'classnames';

import {Button} from 'src/shared/ui/Button';
import {useLocale} from 'src/providers/LocaleProvider';
import useWindowSize from 'src/hooks/useWindowSize';
import useStores from 'src/hooks/useStores';
import paths from 'src/consts/paths';
import videoCameraIcon from 'src/pages/lobby/assets/videoCameraIcon.svg';
import {Searching} from '../Players/Searching';
import {MAX_XL_TABLET} from 'src/pages/game-new2/constants';
import {ModalTypes} from 'src/pages/landing/utils/context';
import {useModal} from 'src/pages/landing/utils/context';
import {usePreciesTimer} from 'src/hooks/usePreciseTimer';

import {Divider} from '../../Divider';

import {useStyles} from './styles';
import {IUserNew} from 'src/store/models';
import GameStore from 'src/store/viewGameStore';
import {boardRoot} from "../../../../../core/endpoints";
import {goTo} from "../../../../../helpers";

interface IPlayer {
    id: string;
    avatarUrl: string;
    nickname: string;
}
interface IPlayOrWatchButton {
    id: string;
    player1: IPlayer;
    player2?: IPlayer | undefined;
    onClick?: (() => Promise<void>) | ((params: any) => Promise<void>);
    isMobile?: boolean;
    createdAt?: string;
}

export const PlayOrWatchButton = ({
    id,
    onClick,
    player1,
    player2,
    isMobile,
    createdAt,
}: IPlayOrWatchButton) => {
    const classes = useStyles();
    const {authStore, lobbyStore, viewHistoryGameStore, gameStore} =
        useStores();
    const me = authStore.currentUser;
    const history = useHistory();
    const {width} = useWindowSize();
    const isAuthorized = authStore.isAuthorized;
    const bonus = authStore?.currentUser?.balance.bonus;
    const play = authStore?.currentUser?.balance.play;
    const free = authStore?.currentUser?.balance.coins;
    const {openModal} = useModal();

    const connectToFreeGame = lobbyStore.connectToFreeGame;
    const {
        lobby: {
            userInteraction: {playGame, cancelGame, stopGame},
            createGame: {
                buttons: {toGame},
            },
        },
    } = useLocale();

    const [isClicking, setIsClicking] = useState<boolean>(false);

    const handleCancelClick = useCallback(
        (id: string) => {
            if (!isClicking) {
                setIsClicking(true);
                lobbyStore
                    .cancelNormalInvite(id)
                    .finally(() => setIsClicking(false));
            }
        },
        [isClicking, id]
    );

    const isMyGame = (player1: IPlayer, player2: IPlayer | undefined) => {
        if (player1 && player2) {
            return player1.id === me?._id || player2.id === me?._id;
        }
        return false;
    };

    const cancelButton =
        player1.id && me && me._id === player1.id && !player2?.id;

    const isParticipating = useMemo(
        () => isMyGame(player1, player2),
        [player1, player2]
    );

    const createdTime = createdAt ? Date.parse(createdAt) : 0;
    const updateTime = 1000;
    const fiveMinutes = 300000;
    const threeMinutes = 180000;
    const tenSeconds = 30000;
    const threeSeconds = 3000;

    const preciesTimer = usePreciesTimer(updateTime, !!cancelButton);

    const timerForCancel = (preciesTimer: number) => {
        if (!preciesTimer && !createdTime) return '--:--';
        const baseTime =
            fiveMinutes <=
            fiveMinutes - (preciesTimer - createdTime) + updateTime
                ? fiveMinutes
                : fiveMinutes - (preciesTimer - createdTime) + updateTime;
        return !!preciesTimer
            ? moment.utc(baseTime).format('m:ss')
            : moment.utc(fiveMinutes).format('m:ss');
    };

    const buttonType = (): JSX.Element => {
        if (player1.id && player2?.id && !isParticipating) {
            return (
                <img
                    src={videoCameraIcon}
                    alt='watch'
                    loading='lazy'
                    className={cn(classes.button, 'watch', {
                        isMobile: isMobile,
                    })}
                    onClick={() => {
                        width && width < MAX_XL_TABLET
                            ? (goTo(boardRoot + `/game/${id}?userKey=${localStorage.getItem("t")}&currentUserId=${authStore.currentUser?._id}`))
                            : viewHistoryGameStore.setGameIdNewView(id);
                    }}
                />
            );
        }
        if (player1.id && player2?.id && isParticipating) {
            return (
                <Button
                    text={toGame('To Game')}
                    className={cn(classes.button, 'toGame', {
                        isMobile: isMobile,
                    })}
                    onClick={() => {
                        goTo(boardRoot + `/game/${id}?userKey=${localStorage.getItem("t")}&currentUserId=${authStore.currentUser?._id}`); return;
                    }}
                    disabled={!isParticipating}
                />
            );
        }
        if (cancelButton) {
            return (
                <Button
                    text={stopGame('Stop')}
                    className={cn(classes.button, 'cancel', {
                        isMobile: isMobile,
                    })}
                    color='red'
                    variant='outlined'
                    onClick={() => handleCancelClick(id)}
                    disabled={isClicking}
                    additionalInfo={timerForCancel(preciesTimer)}
                />
            );
        }
        if (!cancelButton) {
            return (
                <Button
                    text={playGame('Play')}
                    className={cn(classes.button, 'play', {
                        isMobile: isMobile,
                    })}
                    onClick={() => {

                        const isFreePlay = document.location.href.indexOf('lobby-free-play') > 0;
                        if (isAuthorized && (!play && !isFreePlay && !bonus || isFreePlay && !free)) {
                            return history.push(paths.cashbox);
                        }
                        if (isAuthorized) {
                            return connectToFreeGame(id);
                        }
                        if (!isAuthorized) {
                            openModal('login');
                        }
                    }}
                    disabled={isClicking}
                />
            );
        }
        return <></>;
    };

    const ButtonTypeMemo = useMemo(
        () => buttonType(),
        [player1, player2, me, isClicking, preciesTimer]
    );

    return (
        <div
            className={cn(classes.wrapperPlayOrWatchButton, {
                isMobile: isMobile,
            })}
        >
            {!isMobile && <Divider left={0} />}
            {ButtonTypeMemo}
        </div>
    );
};
